// billingSettingsTabs.service — the `/billing/settings` tab registry.
//
// The Billing menu was regrouped to four entries (Overview, Bills, Scheduler,
// Settings). The setup screens that used to be their own menu entries —
// Calendars, the scheduler's Schedule + Rules cards, the exception-check
// configuration and the dismissed log — are tabs on one Settings page now.
//
// This file owns the ORDER of those tabs, their i18n label keys, and the one
// helper every link and redirect uses to build a Settings URL, so a tab id is
// spelled in exactly one place. `CrmTabs` reads `?tab=`; an unknown or absent
// value falls back to the first tab (General).
//
// CLAUDE.md: no user-facing string is returned from here — only i18n KEYS.

/** Every tab on the Billing Settings page, in display order. */
export type BillingSettingsTabId =
  | 'general'
  | 'calendars'
  | 'schedule'
  | 'exceptions'
  | 'dismissed';

export const BILLING_SETTINGS_TAB_ORDER: readonly BillingSettingsTabId[] = [
  'general',
  'calendars',
  'schedule',
  'exceptions',
  'dismissed',
] as const;

/** The landing tab when `?tab=` is absent or unknown. */
export const DEFAULT_BILLING_SETTINGS_TAB: BillingSettingsTabId = 'general';

/** The `?tab=` query key the Settings page reads. */
export const BILLING_SETTINGS_TAB_QUERY_KEY = 'tab';

export const BILLING_SETTINGS_PATH = '/billing/settings';

const LABEL_KEY_OF: Readonly<Record<BillingSettingsTabId, string>> = {
  general: 'billing.settingsPage.tabs.general',
  calendars: 'billing.settingsPage.tabs.calendars',
  schedule: 'billing.settingsPage.tabs.schedule',
  exceptions: 'billing.settingsPage.tabs.exceptions',
  dismissed: 'billing.settingsPage.tabs.dismissed',
};

/** The i18n key for a Settings tab's label. */
export function billingSettingsTabLabelKey(id: BillingSettingsTabId): string {
  return LABEL_KEY_OF[id];
}

/**
 * The in-app URL of one Settings tab. Every link and every legacy-path redirect
 * goes through here, so renaming a tab id cannot leave a stale link behind.
 */
export function billingSettingsHref(tab: BillingSettingsTabId): string {
  return `${BILLING_SETTINGS_PATH}?${BILLING_SETTINGS_TAB_QUERY_KEY}=${encodeURIComponent(tab)}`;
}

/**
 * Legacy Billing paths → the Settings tab that replaced them. The routes table
 * turns each entry into a `<Navigate replace>` so bookmarks keep working.
 */
export const LEGACY_BILLING_SETTINGS_REDIRECTS: ReadonlyArray<{
  readonly from: string;
  readonly tab: BillingSettingsTabId;
}> = [
  { from: '/billing/calendars', tab: 'calendars' },
  { from: '/billing/exceptions', tab: 'exceptions' },
  { from: '/billing/exceptions/log', tab: 'dismissed' },
];
